import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import auth, { AuthContext, type AuthContextValue } from '~/auth';
import { NavLink } from '~/lib/react-router-dom';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  AppBar,
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { TENANT_COLOURS } from '~/consts/tenant';

import useAppNavigation from '~/hooks/useAppNavigation';
import useLocalStorage from '~/hooks/useLocalStorage';
import useNavigationLayoutPreference, { NAVIGATION_LAYOUTS } from '~/hooks/useNavigationLayoutPreference';
import useToggleState from '~/hooks/useToggleState';

import { SiteHeader } from '../SiteHeader';
import { StickyVerifyCustomer } from '../StickyVerifyCustomer';
import TenantSelect from '../TenantSelect';

import AdminSidebar, {
  SIDEBAR_DEFAULT_EXPANDED_WIDTH,
  SIDEBAR_MAX_EXPANDED_WIDTH,
  SIDEBAR_MIN_EXPANDED_WIDTH,
  SIDEBAR_TOP_OFFSET_CSS_VARIABLE,
} from './AdminSidebar';
import NavigationLayoutMenuItem from './NavigationLayoutMenuItem';
import SectionNavigationMenuItem from './SectionNavigationMenuItem';
import SpotlightSearchButton from './SpotlightSearchButton';
import ThemeModeMenuItem from './ThemeModeMenuItem';

interface AppChromeProps {
  children: ReactNode;
  tenant: App.Tenant;
}

const SIDEBAR_TOPBAR_MIN_HEIGHT = 48;
const STICKY_VERIFY_CUSTOMER_SELECTOR = '[data-admin-sticky-verify-customer]';
const SIDEBAR_EXPANDED_WIDTH_STORAGE_KEY = 'adminSidebarExpandedWidth';

function clampExpandedWidth(width: unknown): number {
  if (typeof width !== 'number' || !Number.isFinite(width)) {
    return SIDEBAR_DEFAULT_EXPANDED_WIDTH;
  }
  return Math.min(SIDEBAR_MAX_EXPANDED_WIDTH, Math.max(SIDEBAR_MIN_EXPANDED_WIDTH, width));
}

function getElementBottomOffset(element: Element | null) {
  return Math.max(0, Math.ceil(element?.getBoundingClientRect().bottom ?? 0));
}

export default function AppChrome({ children, tenant }: AppChromeProps) {
  const { user } = useContext<AuthContextValue>(AuthContext);
  const [navigationLayout] = useNavigationLayoutPreference();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [storedExpandedWidth, setStoredExpandedWidth] = useLocalStorage(
    SIDEBAR_EXPANDED_WIDTH_STORAGE_KEY,
    SIDEBAR_DEFAULT_EXPANDED_WIDTH,
  );
  const sidebarExpandedWidth = clampExpandedWidth(storedExpandedWidth);
  const sidebarExpandedWidthRef = useRef(sidebarExpandedWidth);
  sidebarExpandedWidthRef.current = sidebarExpandedWidth;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { push } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();
  const sidebarLayoutRef = useRef<HTMLDivElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const userMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const { isToggled: isUserMenuOpen, toggleOn: openUserMenu, toggleOff: closeUserMenu } = useToggleState();

  const logUserOut = useCallback(() => {
    void auth
      .logout()
      .then(() => {
        push('/');
      })
      .catch((error) => {
        enqueueSnackbar(error instanceof Error ? error.message : 'Log out failed', {
          variant: 'error',
        });
      });
  }, [enqueueSnackbar, push]);

  const toggleSidebar = () => {
    setIsSidebarExpanded((currentValue) => !currentValue);
  };

  const handleSidebarResize = useCallback(
    ({ isExpanded, expandedWidth }: { isExpanded: boolean; expandedWidth: number }) => {
      setIsSidebarExpanded(isExpanded);
      const clamped = clampExpandedWidth(expandedWidth);
      if (clamped !== sidebarExpandedWidthRef.current) {
        setStoredExpandedWidth(clamped);
      }
    },
    [setStoredExpandedWidth],
  );

  useEffect(() => {
    if (navigationLayout === NAVIGATION_LAYOUTS.HEADER || !isDesktop) {
      return;
    }

    let animationFrame = 0;

    const updateSidebarTopOffset = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const topBarBottomOffset = getElementBottomOffset(topBarRef.current);
        const stickyVerifyBottomOffset = getElementBottomOffset(
          document.querySelector(STICKY_VERIFY_CUSTOMER_SELECTOR),
        );
        const nextTopOffset = Math.max(topBarBottomOffset, stickyVerifyBottomOffset);

        sidebarLayoutRef.current?.style.setProperty(SIDEBAR_TOP_OFFSET_CSS_VARIABLE, `${nextTopOffset}px`);
      });
    };

    updateSidebarTopOffset();
    window.addEventListener('scroll', updateSidebarTopOffset, { passive: true });
    window.addEventListener('resize', updateSidebarTopOffset);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('scroll', updateSidebarTopOffset);
      window.removeEventListener('resize', updateSidebarTopOffset);
    };
  }, [isDesktop, navigationLayout]);

  if (navigationLayout === NAVIGATION_LAYOUTS.HEADER || !isDesktop) {
    return (
      <>
        <SiteHeader tenant={tenant} />
        {children}
      </>
    );
  }

  return (
    <>
      <StickyVerifyCustomer />
      <AppBar
        ref={topBarRef}
        position="static"
        sx={{
          background: TENANT_COLOURS[tenant.value].bg,
          color: TENANT_COLOURS[tenant.value].fg,
          zIndex: 9,
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: SIDEBAR_TOPBAR_MIN_HEIGHT,
            px: 2,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ width: '100%' }}>
            <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 220 }}>
              <TenantSelect size="large" />
            </Box>

            <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center', minWidth: 0 }}>
              <SpotlightSearchButton />
            </Box>

            <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'flex-end', minWidth: 220 }}>
              {!user && (
                <Button to="/login" component={NavLink} sx={{ textTransform: 'none' }}>
                  Login
                </Button>
              )}
              {!!user && (
                <>
                  <Button
                    className="T-account"
                    color="inherit"
                    aria-label={`Account menu for ${user.givenName}`}
                    ref={userMenuTriggerRef}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={openUserMenu}
                    sx={{ textTransform: 'none' }}
                  >
                    {user.givenName}
                  </Button>

                  <Menu
                    anchorEl={userMenuTriggerRef.current}
                    open={isUserMenuOpen}
                    autoFocus={false}
                    MenuListProps={{
                      subheader: <ListSubheader>{user.fullName}</ListSubheader>,
                    }}
                    onClose={closeUserMenu}
                  >
                    <MenuItem to="/profile" component={NavLink} onClick={closeUserMenu}>
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText>Account Settings</ListItemText>
                    </MenuItem>

                    <NavigationLayoutMenuItem targetLayout={NAVIGATION_LAYOUTS.HEADER} onClick={closeUserMenu} />
                    <SectionNavigationMenuItem onClick={closeUserMenu} />
                    <ThemeModeMenuItem />

                    <Divider />

                    <MenuItem onClick={logUserOut}>
                      <ListItemIcon>
                        <LogoutIcon />
                      </ListItemIcon>
                      <ListItemText>Log out</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box
        ref={sidebarLayoutRef}
        sx={{
          [SIDEBAR_TOP_OFFSET_CSS_VARIABLE]: `${SIDEBAR_TOPBAR_MIN_HEIGHT}px`,
          display: 'flex',
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        <AdminSidebar
          isExpanded={isSidebarExpanded}
          expandedWidth={sidebarExpandedWidth}
          tenant={tenant}
          user={user}
          onToggle={toggleSidebar}
          onResize={handleSidebarResize}
        />
        <Box component="main" sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
          {children}
        </Box>
      </Box>
    </>
  );
}
